import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowRight, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';

const STEPS = [
  { id: 1, label: 'You' },
  { id: 2, label: 'Payment' },
];

const TestimonialPaymentSubmitPage = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('zelle');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    mailingAddress: '',
    zelleEmailOrPhone: '',
    zelleRegisteredName: '',
    achAccountHolderName: '',
    achBankName: '',
    achRoutingNumber: '',
    achAccountNumber: '',
    achAccountType: 'checking',
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const validateStep = (s) => {
    if (s === 1) {
      const required = ['fullName', 'email', 'phone', 'mailingAddress'];
      for (const f of required) {
        if (!formData[f].trim()) return 'Please complete all required fields.';
      }
    }
    if (s === 2) {
      if (paymentMethod === 'zelle') {
        if (!formData.zelleEmailOrPhone.trim() || !formData.zelleRegisteredName.trim()) {
          return 'Please complete the Zelle fields.';
        }
      } else {
        if (
          !formData.achAccountHolderName.trim() ||
          !formData.achBankName.trim() ||
          !formData.achRoutingNumber.trim() ||
          !formData.achAccountNumber.trim() ||
          !formData.achAccountType
        ) {
          return 'Please complete the ACH fields.';
        }
      }
    }
    return '';
  };

  const handleNext = () => {
    const err = validateStep(step);
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError('');
    setStep(s => Math.min(STEPS.length, s + 1));
  };

  const handleBack = () => {
    setSubmitError('');
    setStep(s => Math.max(1, s - 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateStep(STEPS.length);
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiUrl =
        process.env.REACT_APP_API_URL || (isLocalhost ? 'http://localhost:8001' : '');

      const body = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        mailingAddress: formData.mailingAddress,
        paymentMethod,
      };

      if (paymentMethod === 'zelle') {
        body.zelleEmailOrPhone = formData.zelleEmailOrPhone;
        body.zelleRegisteredName = formData.zelleRegisteredName;
      } else {
        body.achAccountHolderName = formData.achAccountHolderName;
        body.achBankName = formData.achBankName;
        body.achRoutingNumber = formData.achRoutingNumber;
        body.achAccountNumber = formData.achAccountNumber;
        body.achAccountType = formData.achAccountType;
      }

      const response = await fetch(`${apiUrl}/api/testimonial-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || 'Failed to submit');
      }

      setIsSuccess(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <Header />

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>

      <main className="pt-24 pb-16 px-4 relative overflow-hidden min-h-[80vh]">
        {/* Background */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50 via-white to-indigo-50/50"></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-purple-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-[500px] h-[500px] bg-indigo-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto max-w-2xl">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold leading-tight text-slate-900 tracking-tight">
              Testimonial{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Payment Form
              </span>
            </h1>
            <p className="text-sm md:text-base text-slate-500 mt-3 max-w-lg mx-auto">
              Please complete and submit so we can send your testimonial payment.
            </p>
          </div>

          <Card className="shadow-2xl border-0 ring-1 ring-slate-900/5 bg-white/90 backdrop-blur-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600"></div>

            {!isSuccess && (
              <div className="px-6 pt-5 pb-2">
                {/* Stepper */}
                <div className="flex items-center justify-between gap-2">
                  {STEPS.map((s, i) => {
                    const isComplete = step > s.id;
                    const isCurrent = step === s.id;
                    return (
                      <React.Fragment key={s.id}>
                        <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                          <div
                            className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                              isComplete
                                ? 'bg-blue-600 text-white'
                                : isCurrent
                                ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                                : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {isComplete ? <CheckCircle2 className="w-4 h-4" /> : s.id}
                          </div>
                          <span className={`text-[10px] font-semibold uppercase tracking-wider ${isCurrent ? 'text-blue-600' : 'text-slate-400'}`}>
                            {s.label}
                          </span>
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className={`flex-1 h-0.5 -mt-5 rounded ${step > s.id ? 'bg-blue-600' : 'bg-slate-200'}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            <CardHeader className="pb-3 pt-5 px-6">
              <CardTitle className="text-lg font-bold text-slate-900">
                {isSuccess
                  ? 'Submitted'
                  : step === 1
                  ? 'Your Information'
                  : 'Payment Method'}
              </CardTitle>
              <CardDescription className="text-slate-500 text-sm mt-1">
                {isSuccess
                  ? 'Your payment details were received.'
                  : step === 1
                  ? 'Tell us who we should pay.'
                  : 'Choose Zelle (fastest) or ACH bank transfer.'}
              </CardDescription>
            </CardHeader>

            <CardContent className="px-6 pb-6">
              {isSuccess ? (
                <div className="space-y-4 py-4 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-emerald-100 bg-emerald-50">
                    <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-900">
                      Thanks — your payment details were received.
                    </p>
                    <p className="text-sm text-slate-500">
                      Our team will process your payment shortly. We&apos;ll reach out at the email you provided
                      if we need anything else.
                    </p>
                  </div>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit}>
                  {step === 1 && (
                    <>
                      <div className="space-y-1.5">
                        <Label htmlFor="fullName" className="text-xs font-semibold text-slate-700">Full legal name *</Label>
                        <Input id="fullName" placeholder="Jane Doe" value={formData.fullName} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Email *</Label>
                          <Input id="email" type="email" value={formData.email} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="phone" className="text-xs font-semibold text-slate-700">Phone *</Label>
                          <Input id="phone" type="tel" value={formData.phone} onChange={handleInputChange} required className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="mailingAddress" className="text-xs font-semibold text-slate-700">Mailing address *</Label>
                        <Textarea id="mailingAddress" value={formData.mailingAddress} onChange={handleInputChange} required rows={2} className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all text-sm resize-none" />
                      </div>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('zelle')}
                          className={`text-left px-3 py-2.5 rounded-lg border transition-all ${
                            paymentMethod === 'zelle'
                              ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          <div className="text-xs font-bold text-slate-900">Zelle</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">Fastest</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('ach')}
                          className={`text-left px-3 py-2.5 rounded-lg border transition-all ${
                            paymentMethod === 'ach'
                              ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          <div className="text-xs font-bold text-slate-900">ACH / Bank Transfer</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">1–3 business days</div>
                        </button>
                      </div>

                      {paymentMethod === 'zelle' ? (
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="zelleEmailOrPhone" className="text-xs font-semibold text-slate-700">Zelle email or US mobile # *</Label>
                            <Input id="zelleEmailOrPhone" value={formData.zelleEmailOrPhone} onChange={handleInputChange} className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor="zelleRegisteredName" className="text-xs font-semibold text-slate-700">Name registered to Zelle *</Label>
                            <Input id="zelleRegisteredName" value={formData.zelleRegisteredName} onChange={handleInputChange} className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="achAccountHolderName" className="text-xs font-semibold text-slate-700">Account holder name *</Label>
                            <Input id="achAccountHolderName" value={formData.achAccountHolderName} onChange={handleInputChange} className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor="achBankName" className="text-xs font-semibold text-slate-700">Bank name *</Label>
                            <Input id="achBankName" value={formData.achBankName} onChange={handleInputChange} className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="achRoutingNumber" className="text-xs font-semibold text-slate-700">Routing number *</Label>
                              <Input id="achRoutingNumber" inputMode="numeric" value={formData.achRoutingNumber} onChange={handleInputChange} className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="achAccountNumber" className="text-xs font-semibold text-slate-700">Account number *</Label>
                              <Input id="achAccountNumber" inputMode="numeric" value={formData.achAccountNumber} onChange={handleInputChange} className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm" />
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor="achAccountType" className="text-xs font-semibold text-slate-700">Account type *</Label>
                            <select
                              id="achAccountType"
                              value={formData.achAccountType}
                              onChange={handleInputChange}
                              className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-blue-500 focus:outline-none transition-all h-9 text-sm rounded-md px-3"
                            >
                              <option value="checking">Checking</option>
                              <option value="savings">Savings</option>
                            </select>
                          </div>
                        </div>
                      )}

                      <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
                        Optional: you may also include a completed IRS Form W-9 (
                        <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">
                          irs.gov/pub/irs-pdf/fw9.pdf
                        </a>
                        ). Not required — only needed if total payments to you in a calendar year reach the IRS reporting threshold.
                      </p>
                    </>
                  )}

                  {submitError && (
                    <p className="text-sm text-red-600" role="alert">
                      {submitError}
                    </p>
                  )}

                  <div className="flex items-center gap-3 pt-3">
                    {step > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleBack}
                        disabled={isSubmitting}
                        className="h-10 px-4 text-sm font-semibold"
                      >
                        <ArrowLeft className="mr-2 w-4 h-4" />
                        Back
                      </Button>
                    )}
                    {step < STEPS.length ? (
                      <Button
                        type="button"
                        onClick={handleNext}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-10 text-sm font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all"
                      >
                        Continue
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-10 text-sm font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit Payment Details
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="text-[11px] text-center text-slate-400 pt-4">
            Asha Health, Inc. • Confidential — your banking and tax details are used only to process this payment.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TestimonialPaymentSubmitPage;
